import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { formatMessagesPanelTabs } from './utils/messagesPanelUtils';
import { logActivity } from './utils/logActivity';
import { useMarketDataSync } from './utils/marketDataSync';
import { appendJournalEntry, loadJournal, saveJournal, resetJournal } from './utils/studyJournalUtils';
import { JOURNAL_TAB_INDEX, SUMMARY_TAB_INDEX } from './components/MessagesPanelDialog';
import ElliottWaveDialog from './components/ElliottWaveDialog';
import { buildElliottRequest, MIN_BARS_FOR_ANALYSIS } from './utils/elliottRequest';
import { buildElliottOverlay, ELLIOTT_OVERLAY_ID } from './utils/elliottOverlay';
import { buildTrendPulseOverlay, trendPulseOverlaysToEvict } from './utils/trendPulseOverlays';

import { extendBarData } from './utils/extendBarData';
import { addAreaHighlightOverlaysAndMessages } from './components/addAreaHighlightOverlaysUtil';
import HorizontalLineDialog from './components/HorizontalLineDialog';
import TabPanel from './components/TabPanel';
import EChartComponent, { computeRSI } from './components/EChartComponent';
import Login from './components/Login';
import RegistrationPending from './components/RegistrationPending';
import PaymentPending from './components/PaymentPending';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ChartOverlayProvider } from './context/ChartOverlayContext';
import { ConfigProvider, useConfig } from './context/ConfigContext';
import { useChartOverlays } from './context/ChartOverlayContext';
import { HelpProvider } from './context/HelpContext';
import HelpViewer from './components/HelpViewer';

import SymbolList from './components/SymbolList';
import { AppBar, Toolbar, CircularProgress } from '@mui/material';
import UserMenu from './components/UserMenu';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import { watermarkForMarket } from './utils/branding';
import { DEFAULT_DRAWING_COLOR, withAlpha } from './utils/drawingColors';
import { MANUAL_DRAW_SOURCE, upsertTrendlineSummary } from './utils/trendlineMeasure';
import { textSizeAt, activateTextTool, isSizedTool } from './utils/textAnnotationSizes';
import { newOverlayGroupId, lastOverlaySize } from './utils/drawingStack';
import { buildAdditionSeriesStudy, buildFibPivotStudy } from './utils/pivotStudies';
import { nextChartView, normalizeChartView } from './utils/chartViewMode';
import { buildRecentPivotProjection, hasRecentFibOverlays, withoutRecentFibOverlays } from './utils/recentPivotProjection';
import { buildFibTimeOverlays } from './utils/fibTimeProjection';
import { buildFixedCycleOverlays, withoutFixedCycles } from './utils/fixedCycles';
import FixedCycleDialog from './components/FixedCycleDialog';
import {
  TOOL_CLICKS, TECHNICAL_DRAW_SOURCE, fibonacciSegments,
  extendedTrendlineEnd, pitchfork, clampIndex, FUTURE_PROJECTION_BARS,
} from './utils/technicalDrawings';
import { ThemeProvider } from '@mui/material/styles';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import ProtectedRoute from './components/ProtectedRoute';

import { coolWhiteTheme } from './themes';

import LongitudeStudyDlg from './components/LongitudeStudyDlg';
import PatternConfigDialog from './components/PatternConfigDialog';
import PlanetFollowStudyDialog from './components/PlanetFollowStudyDialog';
import AspectsAreaHighlightDialog from './components/AspectsAreaHighlightDialog';

import DeclinationStudyDialog from './components/DeclinationStudyDialog';
import PlanetaryLinesDialog from './components/PlanetaryLinesDialog';

import PlanetGroupDialog from './components/PlanetGroupDialog';
import VisualUniverseDialog from './components/VisualUniverseDialog';
import IntradayAnimationDialog from './components/IntradayAnimationDialog';
import ChartComparisonDialog from './components/ChartComparisonDialog';
import AreaHighlightDialog from './components/AreaHighlightDialog';
import AggregateAreaHighlightDialog from './components/AggregateAreaHighlightDialog';
import StrategyReportStudyDialog from './components/StrategyReportStudyDialog';
import StrategyReportStudyBDialog from './components/StrategyReportStudyBDialog';
import StrategyDesignDialog from './components/StrategyDesignDialog';
import Strategy2DesignDialog from './components/Strategy2DesignDialog';
import TimeAndEventStrategyDialog from './components/TimeAndEventStrategyDialog';
import LocationSettingsDialog from './components/LocationSettingsDialog';
import CyclesDialog from './components/CyclesDialog';
import PlanetCharacteristicsPlotDialog from './components/PlanetCharacteristicsPlotDialog';
import AstroGraphDialog from './components/AstroGraphDialog';
import TrialWelcomeDialog from './components/TrialWelcomeDialog';

import PropTypes from 'prop-types';

function StatusBar({ statusText }) {
  return (
    <div style={{
      width: '100%',
      height: 32,
      background: '#f3f4f6',
      borderTop: '1px solid #e5e7eb',
      color: '#334155',
      fontSize: 14,
      display: 'flex',
      alignItems: 'center',
      paddingLeft: 16,
      boxSizing: 'border-box',
    }}>
      Status: {statusText || 'Ready'}
    </div>
  );
}

StatusBar.propTypes = {
  statusText: PropTypes.string
};

const unwrapDegrees = (degrees) => {
  if (!Array.isArray(degrees) || degrees.length === 0) return [];
  const out = new Array(degrees.length);
  let offset = 0;
  out[0] = degrees[0];
  for (let i = 1; i < degrees.length; i++) {
    const prevRaw = degrees[i - 1];
    const currRaw = degrees[i];
    const diff = currRaw - prevRaw;
    if (diff < -180) {
      offset += 360;
    } else if (diff > 180) {
      offset -= 360;
    }
    out[i] = currRaw + offset;
  }
  return out;
};


function App() {
  return (
    <ConfigProvider>
      <Router>
        <AuthProvider>
          <HelpProvider>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/registration-pending" element={<RegistrationPending />} />
              <Route path="/payment-pending" element={<PaymentPending />} />
              <Route path="/*" element={<ProtectedRoute />}>
                <Route path="*" element={<SessionScope />} />
              </Route>
            </Routes>
          </HelpProvider>
        </AuthProvider>
      </Router>
    </ConfigProvider>
  );
}

/**
 * One clean slate per signed-in account.
 *
 * ProtectedRoute already unmounts MainApp on logout, so MainApp's own state resets. The overlay
 * context did not: it was mounted at the root, above AuthProvider, where it could neither see the
 * user nor be reset — so the studies one account drew were still on the chart when another account
 * signed in.
 *
 * Keying on uid remounts the provider (and everything under it) whenever the account changes,
 * which resets that state by construction rather than by remembering to clear each piece. The
 * 'anon' fallback keeps the key stable while the profile is still loading, so a session is not
 * torn down and rebuilt mid-login.
 */
function SessionScope() {
  const { user } = useAuth();
  return (
    <ChartOverlayProvider key={user?.uid || 'anon'}>
      <MainApp />
    </ChartOverlayProvider>
  );
}

function MainApp() {
  const { user, isTrial, isAdmin, isExpired, isAuthenticated, token, api } = useAuth();

  // Always-current handle on `api`, for effects that need to CALL it but must not re-run when it
  // changes identity. `api` is a useMemo over the Firebase ID token, so it is a new object every
  // time the token rotates (~hourly) — an event that says nothing about the data. Assigned during
  // render rather than in an effect so it is guaranteed current before any effect below reads it.
  const apiRef = useRef(api);
  apiRef.current = api;

  const { config: appConfig } = useConfig();
  const [trialWelcomeOpen, setTrialWelcomeOpen] = useState(false);
  useEffect(() => {
    if (isTrial && user?.uid) {
      const key = `trialWelcomeSeen_${user.uid}`;
      if (!sessionStorage.getItem(key)) {
        setTrialWelcomeOpen(true);
        sessionStorage.setItem(key, '1');
      }
    }
  }, [isTrial, user?.uid]);
    const [srDialogOpen, setSrDialogOpen] = useState(false);
  const [showRSI, setShowRSI] = useState(false);
  const [showMACD, setShowMACD] = useState(false);
  const [isLocationSettingsOpen, setLocationSettingsOpen] = useState(false);
  const [locationPreferences, setLocationPreferences] = useState(null);
  useEffect(() => {
    const fetchLocationPreferences = async () => {
      if (isAuthenticated) {
        try {
          const response = await api.get('/api/user/preferences');
          setLocationPreferences(response.data);
        } catch (error) {
          console.error('Error fetching location preferences:', error);
        }
      }
    };

    fetchLocationPreferences();
  }, [isAuthenticated, api]);

  const handleSaveLocationSettings = async (newPreferences) => {
    if (isAuthenticated) {
      try {
        const response = await api.post('/api/user/preferences', newPreferences);
        setLocationPreferences(response.data);
        // You might want to show a success notification here
      } catch (error) {
        console.error('Error saving location preferences:', error);
      }
    }
  };

  function calculateMACD(bars) {
    if (!bars || bars.length < 26) return { macd: [], signal: [], histogram: [] };
    const closes = bars.map(b => Array.isArray(b) ? b[1] : b.close);
    function ema(period) {
      const k = 2 / (period + 1);
      let emaArr = [];
      let prev;
      closes.forEach((close, i) => {
        if (i === 0) {
          prev = close;
          emaArr.push(close);
        } else {
          prev = close * k + prev * (1 - k);
          emaArr.push(prev);
        }
      });
      return emaArr;
    }
    const ema12 = ema(12);
    const ema26 = ema(26);
    const macdLine = ema12.map((v, i) => v - ema26[i]);
    function emaOf(arr, period) {
      const k = 2 / (period + 1);
      let prev;
      let out = [];
      arr.forEach((val, i) => {
        if (i === 0) {
          prev = val;
          out.push(val);
        } else {
          prev = val * k + prev * (1 - k);
          out.push(prev);
        }
      });
      return out;
    }
    const signalLine = emaOf(macdLine, 9);
    const histogram = macdLine.map((v, i) => v - signalLine[i]);
    const macd = macdLine.map((v, i) => ({ time: i, value: v }));
    const signal = signalLine.map((v, i) => ({ time: i, value: v }));
    const hist = histogram.map((v, i) => ({ time: i, value: v }));
    return { macd, signal, histogram: hist };
  }

  const [messagesTabContents, setMessagesTabContents] = useState([
    <div key="summary">Initial Summary Content</div>,
    <div key="message">Initial Message Content</div>,
    <div key="reserved1">Reserved 1</div>,
    <div key="reserved2">Reserved 2</div>
  ]);
  const [messagesOpen, setMessagesOpen] = useState(false);
  // Which tab the Messages panel shows. Owned here so a study can open it on the tab it wrote to.
  const [messagesTabIndex, setMessagesTabIndex] = useState(SUMMARY_TAB_INDEX);
  // Elliott wave analysis is a request/response action rather than a chart overlay, so its result
  // lives here for the panel to render and is replaced by each run.
  const [elliottOpen, setElliottOpen] = useState(false);
  const [elliottLoading, setElliottLoading] = useState(false);
  const [elliottResult, setElliottResult] = useState(null);
  const [elliottError, setElliottError] = useState(null);
  const [elliottOverlayShown, setElliottOverlayShown] = useState(false);
  const [studyTitle, setStudyTitle] = useState('');

  // Study Journal state — persisted to localStorage
  // Journal is per account. MainApp is remounted by SessionScope when the user changes, so the
  // initialiser re-runs with the new uid rather than carrying the previous account's entries.
  const [journalMarkdown, setJournalMarkdown] = useState(() => loadJournal(user?.uid));
  useEffect(() => { saveJournal(journalMarkdown, user?.uid); }, [journalMarkdown, user?.uid]);

  // Owned here rather than inside the dialog so the effect below can tell when the user is typing.
  const [journalEditing, setJournalEditing] = useState(false);


  const handleJournalAppend = useCallback((entryData) => {
    setJournalMarkdown(prev => appendJournalEntry(prev, entryData));
  }, []);

  /**
   * Log a study report AND put the reader in front of it.
   *
   * A study whose only output is the Journal has to open the panel on the Journal tab, or it
   * appears to have done nothing: the panel opens on Summary, which such a study never fills, so
   * a blank Summary is indistinguishable from a study that produced no result. Studies that fill
   * the Summary tab keep using handleJournalAppend and their own panel handling.
   */
  const handleStudyReportJournal = useCallback((entryData) => {
    setJournalMarkdown(prev => appendJournalEntry(prev, entryData));
    setStudyTitle(entryData?.studyLabel || 'Study');
    setMessagesTabIndex(JOURNAL_TAB_INDEX);
    setMessagesOpen(true);
  }, []);
  const handleJournalReset = useCallback(() => {
    if (window.confirm('Reset the Study Journal? This cannot be undone.')) {
      setJournalMarkdown(resetJournal());
    }
  }, []);

  const handleAspectsAreaHighlightRun = useCallback(({ title, stats, futureDates }) => {
    console.log('[App.js] handleAspectsAreaHighlightRun received:', { title, stats, futureDates });
    setStudyTitle(title || 'Aspects Study'); // Set title or a default
    const tabContents = formatMessagesPanelTabs(stats, futureDates);
    setMessagesTabContents(tabContents);
    setMessagesOpen(true);
  }, [setStudyTitle, setMessagesTabContents, setMessagesOpen]);

  
  const handleStrategyDesignRun = useCallback(({ title, tabContents }) => {
    setStudyTitle(title || 'Strategy Design');
    setMessagesTabContents(tabContents);
    setMessagesOpen(true);
  }, [setStudyTitle, setMessagesTabContents, setMessagesOpen]);

  const eChartRef = useRef(null);
  const animateIntervalRef = useRef(null);
  const animatePausedRef = useRef(false);
  const studyAdvanceRef = useRef(null);

  const { overlays, addOverlay, setAllOverlays, removeOverlay, updateOverlay, deleteLastOverlay, clearOverlays } = useChartOverlays();

  // Every figure a drawing tool produces goes through here so it is stamped with a groupId, which
  // is what makes it eligible for undo and what keeps a multi-line figure (Fibonacci, pitchfork)
  // deleting as one unit. Routing all the drawing adds through a single helper means a new tool
  // cannot silently arrive un-undoable by forgetting the field.
  // Always-current overlay list, for callbacks that need to REPLACE the set without taking
  // `overlays` as a dependency — handleBarSelection would otherwise rebuild on every draw.
  // Assigned during render so it is correct before any handler can fire.
  const overlaysRef = useRef(overlays);
  overlaysRef.current = overlays;

  const addDrawing = useCallback((overlay) => {
    addOverlay({ ...overlay, groupId: overlay.groupId || newOverlayGroupId(overlay.type) });
  }, [addOverlay]);

  // Placed AFTER `overlays` is declared, and that ordering is load-bearing: a hook's dependency
  // array is evaluated during render, so listing `overlays` above its own `const` threw
  // "Cannot access 'overlays' before initialization" and took the whole app down to a blank
  // screen. Neither the build nor the unit tests catch it — it is a temporal-dead-zone error at
  // runtime, in a file no test renders.
  //
  // Keeps the journal's trendline section in step with what is on the chart. The section is
  // delimited and rewritten in place, so drawing a fourth line replaces the block rather than
  // stacking a second copy underneath, and anything typed outside the markers is untouched.
  //
  // Skipped while the journal is open for editing: rewriting the text under an open editor would
  // overwrite what is being typed. It catches up the moment editing ends, which is why
  // journalEditing is a dependency and not just an early return.
  useEffect(() => {
    if (journalEditing) return;
    setJournalMarkdown(prev => {
      const next = upsertTrendlineSummary(prev, overlays);
      return next === prev ? prev : next;   // a no-op write would loop through the save effect
    });
  }, [overlays, journalEditing]);
  const [barData, setBarData] = useState({ ohlc: [], dates: [], volumes: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Bumped whenever the market-data-updater reports a finished sync. It is a dependency of
  // the chart data effect below, so a completed sync re-fetches the current symbol instead
  // of leaving the customer looking at yesterday's bars until they refresh the page.
  const [marketDataRevision, setMarketDataRevision] = useState(0);
  const [selectedSymbol, setSelectedSymbol] = useState(appConfig?.defaultTicker || 'SPY');

  // Every overlay belongs to the symbol it was drawn on, so switching symbol clears the chart.
  //
  // Overlays are positioned by DATE. Carried across to another symbol they either land on
  // unrelated bars or, where the date does not exist in the new series, are silently dropped by
  // the renderer — so a study from the previous chart would appear to have been run on this one,
  // partially, with no indication that it had not been.
  //
  // Keyed on selectedSymbol alone, NOT on barData: a background refresh replaces barData for the
  // same symbol, and clearing there would wipe the customer's drawings every time a sync landed.
  const clearedForSymbolRef = useRef(null);
  useEffect(() => {
    // Skip the very first run, which would only clear an already-empty chart, and would also
    // discard anything restored before the first render.
    if (clearedForSymbolRef.current === null) {
      clearedForSymbolRef.current = selectedSymbol;
      return;
    }
    if (clearedForSymbolRef.current === selectedSymbol) return;
    clearedForSymbolRef.current = selectedSymbol;
    clearOverlays();
    setStatusText(`Loaded ${selectedSymbol}. Overlays from the previous chart were cleared.`);
  }, [selectedSymbol, clearOverlays]);


  const [isStudyDialogOpen, setStudyDialogOpen] = useState(false);
  const [isPlanetFollowStudyDialogOpen, setPlanetFollowStudyDialogOpen] = useState(false);
  const [isAspectsAreaHighlightDialogOpen, setIsAspectsAreaHighlightDialogOpen] = useState(false);
  const [isDeclinationStudyDialogOpen, setDeclinationStudyDialogOpen] = useState(false);
  const [isPlanetaryLinesDialogOpen, setPlanetaryLinesDialogOpen] = useState(false);
  const [isVisualUniverseDialogOpen, setVisualUniverseDialogOpen] = useState(false);
  const [visualUniverseInitialDate, setVisualUniverseInitialDate] = useState(null);
  const [isIntradayAnimationDialogOpen, setIntradayAnimationDialogOpen] = useState(false);
  const [isChartComparisonDialogOpen, setChartComparisonDialogOpen] = useState(false);

  const handleOpenVisualUniverse = (date) => {
    setVisualUniverseInitialDate(date);
    setVisualUniverseDialogOpen(true);
  };
  const [isPlanetGroupDialogOpen, setPlanetGroupDialogOpen] = useState(false);
  const [isStrategyReportStudyDialogOpen, setStrategyReportStudyDialogOpen] = useState(false);
  const [isStrategyReportStudyBDialogOpen, setStrategyReportStudyBDialogOpen] = useState(false);
  const [isAreaHighlightDialogOpen, setIsAreaHighlightDialogOpen] = useState(false);
  const [isAggregateAreaHighlightDialogOpen, setIsAggregateAreaHighlightDialogOpen] = useState(false);
  // Fixed-cycle tool: the dialog collects N, then the next chart click anchors it. The pending
  // length lives in state rather than a ref because the toolbar button reflects it when re-opened.
  const [fixedCycleDialogOpen, setFixedCycleDialogOpen] = useState(false);
  const [fixedCycleLength, setFixedCycleLength] = useState(null);
  const [fixedCycleUnit, setFixedCycleUnit] = useState('bars');
  const [isStrategyDesignDialogOpen, setIsStrategyDesignDialogOpen] = useState(false);
  const [isStrategy2DesignDialogOpen, setIsStrategy2DesignDialogOpen] = useState(false);
  const [isTimeAndEventStrategyDialogOpen, setTimeAndEventStrategyDialogOpen] = useState(false);
  const [isCyclesDialogOpen, setCyclesDialogOpen] = useState(false);
  const [isPlanetCharacteristicsDialogOpen, setIsPlanetCharacteristicsDialogOpen] = useState(false);
  const [isAstroGraphDialogOpen, setIsAstroGraphDialogOpen] = useState(false);
  const [astroGraphCommentary, setAstroGraphCommentary] = useState('');
  const [studyMode, setStudyMode] = useState(false);
  const [studyVisibleCount, setStudyVisibleCount] = useState(0);
  const [bar1Date, setBar1Date] = useState(null);
  const [bar2Date, setBar2Date] = useState(null);
  const [nextBarToSelect, setNextBarToSelect] = useState('bar1'); // 'bar1' or 'bar2'
  const [isManualDrawMode, setIsManualDrawMode] = useState(false);
  const [manualDrawStyle, setManualDrawStyle] = useState({ color: '#FF00FF', lineWidth: 3 });

  // Drawing tool state: null | 'draw_vertical_line' | 'draw_horizontal_line' | 'draw_trendline' | 'draw_rectangle'
  const [drawingMode, setDrawingMode] = useState(null);
  // Colour applied to newly drawn shapes and text. Existing drawings keep whatever they were
  // created with, so a chart can be marked up in several colours.
  const [activeDrawingColor, setActiveDrawingColor] = useState(DEFAULT_DRAWING_COLOR);
  const drawingFirstClick = useRef(null); // stores {date, price, dataIndex} for two-click tools
  // Separate accumulator for the multi-click technical tools. Kept apart from drawingFirstClick
  // rather than generalising it, so the vertical/horizontal/trendline/rectangle paths that already
  // work are not disturbed by a tool that needs a third click.
  const drawingPoints = useRef([]);
  // Text annotation size cycles Small -> Medium -> Large on each activation of the T tool.
  // Two pieces of state, deliberately: textSizeIndex is what the NEXT click will use, while
  // activeTextSize is what the current activation draws at. Deriving one from the other at
  // placement time would place text at the next size rather than the one the badge advertised.
  const [textSizeIndex, setTextSizeIndex] = useState(0);
  const [activeTextSize, setActiveTextSize] = useState(() => textSizeAt(0));
  
  const [chartType, setChartType] = useState('candlestick');
  const [barFloaterMode, setBarFloaterMode] = useState('ohlcv');
  // Watermark override for admin: null = default, { hidden: true } = no watermark, { text, fontSize } = custom
  const [watermarkOverride, setWatermarkOverride] = useState(null);
  // The watermark follows the customer's market, not the deployment: US shows AstroStockCharts,
  // India shows Dhanabhyas. Anonymous visitors and the moment before the profile loads fall back
  // to the deployment's own brand.
  // marketId is the stored state, but /api/user/me also returns `market` ("US"/"IN"), and the
  // deployment config carries one too. Taking the first that resolves means a profile missing one
  // of them — or a chart rendered before the profile arrives — still brands correctly.
  const watermarkText = watermarkForMarket(
    user?.marketId ?? user?.market ?? appConfig?.market);
  const handleToggleWatermark = useCallback(() => {
    setWatermarkOverride(prev => {
      if (!prev) return { hidden: true };                             // default → hidden
      if (prev.hidden) return { text: watermarkText, fontSize: 120 }; // hidden → small
      return null;                                                    // small → back to default
    });
  }, [watermarkText]);
  const theme = coolWhiteTheme;
  const [isPatternConfigDialogOpen, setIsPatternConfigDialogOpen] = useState(false);
    const [maConfigs, setMaConfigs] = useState([
    { period: 20, color: '#2979FF', enabled: false },
    { period: 50, color: '#FF9800', enabled: false },
    { period: 200, color: '#4CAF50', enabled: false },
  ]);

  // Removes the most recent addition — a drawn figure or a whole study run. Reports what happened
  // either way: a shortcut that silently does nothing is indistinguishable from one that is broken.
  const handleDeleteLastDrawing = useCallback(() => {
    const removed = lastOverlaySize(overlays);
    if (removed === 0) {
      setStatusText('Nothing to undo — the chart is empty.');
      return;
    }
    deleteLastOverlay();
    setStatusText(removed > 1
      ? `Removed last addition (${removed} overlays).`
      : 'Removed last addition.');
  }, [overlays, deleteLastOverlay]);

  // What the chart is busy computing, or null. Drives the overlay and the wait cursor.
  const [computingLabel, setComputingLabel] = useState(null);

  /**
   * Runs blocking work with a visible busy indicator.
   *
   * The two animation frames are the point. These scans hold the main thread for seconds on a long
   * chart, and JavaScript cannot paint while it is blocked — so setting the state and calling the
   * work in the same tick shows nothing at all, then freezes, then shows the finished result. The
   * user sees an unresponsive page and no explanation. Yielding twice lets React commit the state
   * and lets the browser paint it before the thread is taken.
   *
   * The indicator's spinner is a CSS animation, which keeps turning on the compositor while the
   * main thread is busy, so it stays honest for the whole wait rather than freezing mid-spin.
   */
  const runWithBusyIndicator = useCallback((label, work) => {
    setComputingLabel(label);
    requestAnimationFrame(() => requestAnimationFrame(() => {
      try {
        work();
      } catch (err) {
        console.error('[App.js] study failed:', err);
        setStatusText(`${label} failed: ${err.message}`);
      } finally {
        // In a finally so a thrown error cannot strand the page behind a permanent overlay.
        setComputingLabel(null);
      }
    }));
  }, []);

  // Esc cancels drawing mode; Ctrl/Cmd+Z and Delete/Backspace undo the last drawing.
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Never steal a keystroke from a field the user is typing in. Backspace in the annotation
      // edit box must delete a character, and Ctrl+Z there must undo typing — not silently
      // remove a drawing somewhere else on the chart.
      const el = e.target;
      const tag = el && el.tagName ? el.tagName.toLowerCase() : '';
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || (el && el.isContentEditable)) return;

      if (e.key === 'Escape' && drawingMode) {
        setDrawingMode(null);
        drawingFirstClick.current = null;
        drawingPoints.current = [];
        setStatusText('Drawing cancelled.');
        return;
      }
      // A dialog or popover being open means the keystroke is meant for it, not for the chart
      // underneath. Without this, pressing Delete while a study dialog is up would quietly remove
      // a drawing the user cannot even see, and the status message would scroll past unread.
      // MUI unmounts modals when closed, so this selector only matches while one is actually open.
      if (document.querySelector('.MuiModal-root')) return;

      const isUndo = (e.ctrlKey || e.metaKey) && !e.shiftKey && (e.key === 'z' || e.key === 'Z');
      const isDelete = !e.ctrlKey && !e.metaKey && !e.altKey && (e.key === 'Delete' || e.key === 'Backspace');
      if (isUndo || isDelete) {
        // Backspace outside a field navigates back in some browsers, and Ctrl+Z would reach the
        // browser's own undo stack.
        e.preventDefault();
        handleDeleteLastDrawing();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [drawingMode, handleDeleteLastDrawing]);

  const studyBarData = useMemo(() => {
    if (!studyMode || !barData || !barData.dates || studyVisibleCount <= 0) return barData;
    const count = Math.min(studyVisibleCount, barData.dates.length);
    return {
      ...barData,
      ohlc: barData.ohlc.map((bar, i) => i < count ? bar : [null, null, null, null]),
      volumes: barData.volumes.map((v, i) => i < count ? v : 0),
      realBarCount: count,
    };
  }, [studyMode, barData, studyVisibleCount]);

  const effectiveBarData = studyMode ? studyBarData : barData;
  const extendedBarData = useMemo(() => extendBarData(effectiveBarData, studyMode ? 0 : 250), [effectiveBarData, studyMode]);

  // Declared AFTER extendedBarData, and that ordering is load-bearing. A hook dependency array
  // is evaluated during render, so listing extendedBarData above its own `const` threw
  // "Cannot access 'extendedBarData' before initialization" and took the whole app down on
  // mount. The build does not catch it and no unit test renders this file, so nothing flagged
  // it — the same temporal dead zone trap this component has hit before.

  // The two admin pivot studies. The builders re-check isAdmin themselves — the hidden toolbar
  // button is a convenience, not the guard — and these handlers report the outcome either way,
  // since a study that legitimately finds nothing looks identical to one that silently failed.
  const runAdditionSeries = useCallback(() => {
    const result = buildAdditionSeriesStudy(barData, { isAdmin });
    if (!result) {
      setStatusText('Addition series: no qualifying pivot sequence found on this chart.');
      return;
    }
    addOverlay(result.overlay);
    const { seeds, terms, pivotCount, anchorDate } = result.summary;
    setStatusText(`Addition series from bar 0 (${anchorDate}): seeds ${seeds.join(' + ')}, `
      + `${terms.length} terms, ${pivotCount} pivots marked.`);
  }, [barData, isAdmin, addOverlay]);

  const runFibPivotPairs = useCallback(() => {
    const result = buildFibPivotStudy(barData, { isAdmin, extendedDates: extendedBarData?.dates });
    if (!result) {
      setStatusText('Fibonacci pivot pairs: no qualifying pairs found on this chart.');
      return;
    }
    // One call, so the dots and the base-span verticals form a single undo unit — undoing a study
    // halfway, leaving its lines behind without the dots, would be worse than not undoing at all.
    addOverlay([result.overlay, ...(result.lines || [])]);
    const { pairsFound, pairsUsed, pivotCount, truncated, lineCount, baseSpan, projectionCount, nextTargetBars } = result.summary;
    setStatusText(`Fibonacci pivot pairs: ${pairsFound} pair(s) found`
      + `${truncated ? `, strongest ${pairsUsed} drawn` : ''}, ${pivotCount} pivots marked`
      + `${lineCount ? `, ${lineCount} base-span lines (span ${baseSpan} bars)` : ''}`
      + `${projectionCount ? `, ${projectionCount} projected target(s), next in ${nextTargetBars} bars` : ''}.`);
  }, [barData, extendedBarData, isAdmin, addOverlay]);

  // Toggle rather than add: the projections answer one question about the current state of the
  // chart, so a second set stacked on the first would be the same answer drawn twice. Removing by
  // id prefix is what makes the button idempotent no matter what else has been drawn since.
  const runRecentFibProjections = useCallback(() => {
    if (hasRecentFibOverlays(overlays)) {
      setAllOverlays(withoutRecentFibOverlays(overlays));
      setStatusText('Recent-pivot Fibonacci projections removed.');
      return;
    }
    const result = buildRecentPivotProjection(barData, {
      isAdmin,
      extendedDates: extendedBarData?.dates,
    });
    if (!result) {
      setStatusText('Recent Fibs: need at least two 5-bar pivots on this chart.');
      return;
    }
    // One call, so the anchors, targets, hit dots and guide form a single undo unit.
    addOverlay(result.overlays);
    const { distance, targetCount, futureCount, hitCount, nextTargetBars, p1Date } = result.summary;
    setStatusText(`Recent Fibs from ${p1Date} (interval ${distance} bars): ${targetCount} target(s), `
      + `${futureCount} in the future${nextTargetBars !== null ? `, next in ${nextTargetBars} bars` : ''}`
      + `${hitCount ? `, ${hitCount} already hit` : ''}.`);
  }, [overlays, barData, extendedBarData, isAdmin, addOverlay, setAllOverlays]);

  // Wrapped so the busy indicator is on screen before the scan takes the thread.
  const handleRunPivotAdditionSeries = useCallback(
    () => runWithBusyIndicator('Scanning pivots for an addition series', runAdditionSeries),
    [runWithBusyIndicator, runAdditionSeries]);
  const handleRunFibPivotPairs = useCallback(
    () => runWithBusyIndicator('Scanning pivot pairs for Fibonacci ratios', runFibPivotPairs),
    [runWithBusyIndicator, runFibPivotPairs]);
  const handleToggleRecentFibProjections = useCallback(
    () => runWithBusyIndicator('Projecting from the two most recent pivots', runRecentFibProjections),
    [runWithBusyIndicator, runRecentFibProjections]);

  // Use stable barData.ohlc for RSI/MACD (not study-truncated) to avoid recomputation on every advance
  const stableOhlc = barData?.ohlc;
  const rsiData = useMemo(() => computeRSI(stableOhlc), [stableOhlc]);
  const macdData = useMemo(() => calculateMACD(stableOhlc), [stableOhlc]);

  const handleTimeAndEventStrategyRun = useCallback(async (payload) => {
    console.log('Time and Event Strategy RUN:', payload);
    let intervalId;

    try {
      const fullPayload = {
        ...payload,
        symbol: barData?.symbol,
        startDateTime: extendedBarData?.dates?.[0],
        endDateTime: extendedBarData?.dates?.[extendedBarData.dates.length - 1],
      };

      console.log('Full payload for Time and Event Strategy:', JSON.stringify(fullPayload, null, 2));

      const initialResponse = await api.post('/api/run-time-event-strategy', fullPayload, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const { taskId } = initialResponse.data;

      const pollResult = () => {
        api.get(`/api/run-time-event-strategy/result/${taskId}`, { headers: { 'Authorization': `Bearer ${token}` } })
          .then((response) => {
            if (response.status === 200) {
              clearInterval(intervalId);
              const result = response.data;
              console.log('[App.js] Backend response from /api/run-time-event-strategy:', result);

              const datePairs = Array.isArray(result.datePairs) ? result.datePairs : [];
              const summaryLines = Array.isArray(result.summaryLines) ? result.summaryLines : [];
              const futureDates = Array.isArray(result.futureDates) ? result.futureDates : [];

              addAreaHighlightOverlaysAndMessages({
        isAdmin,
                datePairs,
                summaryLines,
                futureDates,
                params: { color: result.color || 'rgba(33, 150, 243, 0.5)', label: 'Time and Event Strategy' },
                addOverlay,
                barData,
                extendedBarData,
                updateMessagesPanel: ({ summaryLines, futureDates }) => {
                  const tabContents = formatMessagesPanelTabs(summaryLines, futureDates);
                  setStudyTitle('Time and Event Strategy');
                  setMessagesTabContents(tabContents);
                  setMessagesOpen(true);
                },
                onJournalAppend: handleJournalAppend,
              });

              setTimeAndEventStrategyDialogOpen(false);
            }
          })
          .catch((err) => {
            // If the error is a 401, the auth interceptor will handle it.
            // We just need to stop polling.
            if (err.response && err.response.status === 401) {
              console.log('Polling stopped due to session expiration.');
            } else {
              // For other errors, show an alert.
              console.error('[App.js] Polling error:', err);
              alert(`Failed to get result for task ${taskId}: ${err.message}`);
            }
            clearInterval(intervalId);
          });
      };

      intervalId = setInterval(pollResult, 2000);
    } catch (err) {
      console.error('[App.js] RUN error from /api/run-time-event-strategy:', err);
      const errorMessage = err.response?.data?.error || err.message;
      alert(`Failed to run time and event strategy: ${errorMessage}`);
      if (intervalId) {
        clearInterval(intervalId);
      }
    }
  }, [api, token, isAdmin, addOverlay, barData, extendedBarData, setStudyTitle, setMessagesTabContents, setMessagesOpen, setTimeAndEventStrategyDialogOpen, handleJournalAppend]);

  const handleStrategy2DesignRun = useCallback(async ({ studies, color, threshold, token }) => {
    console.log('Strategy 2 RUN:', { studies, color, threshold });
    try {
      const payload = {
        studies: studies, // This should be 'studies' to match the backend
        scoreThreshold: threshold,
        highlightColor: color,
        symbol: barData?.symbol,
        startDateTime: Array.isArray(barData?.dates) && barData.dates.length > 0 ? barData.dates[0] : undefined,
        endDateTime: Array.isArray(extendedBarData?.dates) && extendedBarData.dates.length > 0
          ? extendedBarData.dates[extendedBarData.dates.length - 1]
          : (Array.isArray(barData?.dates) && barData.dates.length > 0 ? barData.dates[barData.dates.length - 1] : undefined)
      };
      console.log('[App.js] Calling /api/run-strategy2 with payload:', payload);
      const response = await api.post('/api/run-strategy2', payload, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const result = response.data;
      console.log('[App.js] Backend response from /api/run-strategy2:', result);

      const datePairs = Array.isArray(result.datePairs) ? result.datePairs : [];
      const summaryLines = Array.isArray(result.summaryLines) ? result.summaryLines : [];
      let futureDates = Array.isArray(result.futureDates) ? result.futureDates : [];

      // Backend returns empty futureDates for Strategy 2; extract from datePairs where endDate is in the future
      if (futureDates.length === 0 && datePairs.length > 0) {
        const today = new Date().toISOString().slice(0, 10);
        futureDates = datePairs.filter(p => p.endDate && p.endDate >= today);
      }

      const formattedFutureDates = futureDates.map(pair => `${pair.startDate}-to-${pair.endDate}`);

      addAreaHighlightOverlaysAndMessages({
        isAdmin,
        datePairs,
        summaryLines,
        futureDates: formattedFutureDates,
        params: { color, label: 'Strategy 2 Design' },
        addOverlay,
        barData,
        extendedBarData,
        updateMessagesPanel: ({ summaryLines, futureDates }) => {
          const tabContents = formatMessagesPanelTabs(summaryLines, futureDates);
          setStudyTitle('Strategy 2 Design');
          setMessagesTabContents(tabContents);
          setMessagesOpen(true);
        },
        onJournalAppend: handleJournalAppend,
      });
      setIsStrategy2DesignDialogOpen(false);
    } catch (err) {
      console.error('[App.js] RUN error from /api/run-strategy2:', err);
      const errorMessage = err.response?.data?.error || err.message;
      alert(`Failed to run strategy design study: ${errorMessage}`);
    }
  }, [api, isAdmin, barData, extendedBarData, addOverlay, setStudyTitle, setMessagesTabContents, setMessagesOpen, setIsStrategy2DesignDialogOpen, handleJournalAppend]);

  const handleAstroGraphExecute = useCallback(async (dialogState) => {
    if (!extendedBarData || !extendedBarData.dates || extendedBarData.dates.length === 0) {
      alert('Chart data not available. Please load a symbol first.');
      return false;
    }
    const startDate = extendedBarData.dates[0].substring(0, 10);
    const endDate   = extendedBarData.dates[extendedBarData.dates.length - 1].substring(0, 10);
    // Trading dates for AstroGraph computation (all chart dates including future extension)
    const priceDates  = (extendedBarData?.dates || []).map(d => d.substring(0, 10));
    // Price closes & dates for commentary engine (actual historical data only)
    const priceCloses = (barData?.ohlc || []).map(bar => bar[3]);
    const commentaryDates = (barData?.dates || []).map(d => d.substring(0, 10));

    const payload = {
      startDate,
      endDate,
      preset:         dialogState.preset,
      enabledPlanets: dialogState.planetEnabled,
      planetAspects:  dialogState.planetAspects,
      conjPolarity:   dialogState.conjPolarity,
      aspectPolarity: dialogState.aspectPolarity,
      planetWeights:  dialogState.planetWeights,
      aspectWeights:  dialogState.aspectWeights,
      planetDecl:     dialogState.planetDecl,
      legend:         dialogState.legend,
      longWeight:     dialogState.longWeight,
      mediumWeight:   dialogState.mediumWeight,
      priceCloses,
      priceDates,
      commentaryDates,
      toleranceDays:  dialogState.toleranceDays ?? 5,
    };
    if (Number.isFinite(dialogState.classicSmoothingDays)) {
      payload.classicSmoothingDays = dialogState.classicSmoothingDays;
    }
    try {
      console.log('[App.js] /api/astrograph payload:', payload);
      const response = await api.post('/api/astrograph', payload, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const { series, commentary, error: backendError } = response.data;
      if (backendError) {
        alert('TrendPulse backend error: ' + backendError);
        return false;
      }
      if (!Array.isArray(series) || series.length === 0) {
        alert('TrendPulse: no data returned from backend.');
        return false;
      }
      // Curves are KEPT, up to MAX_TRENDPULSE_OVERLAYS, so several smoothing presets can be
      // compared on one chart. This used to delete the previous curve before drawing, which meant
      // the study could answer "what does this preset look like" but never "which of these fits
      // this symbol better" — the question a smoothing parameter exists to be asked.
      trendPulseOverlaysToEvict(overlays).forEach(removeOverlay);
      addOverlay(buildTrendPulseOverlay({
        series,
        preset: dialogState.preset,
        overlays,
        color: dialogState.lineColor,
        thickness: dialogState.lineThickness,
        lineType: dialogState.lineType,
      }));
      // Update commentary in dialog (dialog stays open)
      setAstroGraphCommentary(commentary || '');
      return true;
    } catch (err) {
      console.error('[App.js] /api/astrograph error:', err);
      alert(`TrendPulse failed: ${err.response?.data?.error || err.message}`);
      return false;
    }
  }, [api, token, barData, extendedBarData, addOverlay, overlays, removeOverlay]);

  const exitStudyMode = useCallback(() => {
    setStudyMode(false);
    setStudyVisibleCount(0);
    setStatusText('Study mode ended.');
    animatePausedRef.current = false;
    if (animateIntervalRef.current) {
      clearInterval(animateIntervalRef.current);
      animateIntervalRef.current = null;
    }
  }, []);

  const handleStudyAdvance = useCallback(() => {
    setStudyVisibleCount(prev => {
      if (!barData || !barData.dates) return prev;
      const next = prev + 1;
      if (next > barData.dates.length) return prev;
      return next;
    });
  }, [barData]);

  // Track study progress and detect completion (avoids setState-inside-setState)
  useEffect(() => {
    if (!studyMode || !barData || !barData.dates) return;
    if (studyVisibleCount >= barData.dates.length) {
      exitStudyMode();
      return;
    }
    if (studyVisibleCount > 0) {
      const d = barData.dates[studyVisibleCount - 1]?.substring(0, 10) || '';
      setStatusText(`Study mode: bar ${studyVisibleCount} of ${barData.dates.length} (${d})`);
    }
  }, [studyMode, studyVisibleCount, barData, exitStudyMode]);

  // Keep ref in sync so setInterval always calls the latest version
  studyAdvanceRef.current = handleStudyAdvance;

  const handleAstroGraphExecuteAndStudy = useCallback(async (dialogState) => {
    const success = await handleAstroGraphExecute(dialogState);
    if (!success) return;

    if (!barData || !barData.dates || barData.dates.length === 0) return;

    const targetDate = dialogState.studyStartDate;
    if (!targetDate) {
      alert('Please select a study start date.');
      return;
    }

    // Find the nearest trading date index at or before the target date
    let cutoffIdx = -1;
    for (let i = 0; i < barData.dates.length; i++) {
      const d = barData.dates[i].substring(0, 10);
      if (d <= targetDate) cutoffIdx = i;
      else break;
    }
    if (cutoffIdx < 0) {
      alert('No trading dates found at or before the selected date.');
      return;
    }
    const cutoffDate = barData.dates[cutoffIdx].substring(0, 10);

    setStudyVisibleCount(cutoffIdx + 1);
    setStudyMode(true);
    setStatusText(`Study mode: chart starts at ${cutoffDate}. Click to reveal bars.`);
    setIsAstroGraphDialogOpen(false);
    // Deactivate area zoom tool so the "+" cursor doesn't interfere with study clicks
    try {
      const chart = eChartRef.current?.getEchartsInstance?.();
      if (chart) {
        chart.dispatchAction({ type: 'takeGlobalCursor', key: 'dataZoomSelect', dataZoomSelectActive: false });
      }
    } catch (_) { /* ignore */ }
  }, [handleAstroGraphExecute, barData]);

  const handleAstroGraphAnimate = useCallback(async (dialogState) => {
    // Execute the AstroGraph overlay first
    const success = await handleAstroGraphExecute(dialogState);
    if (!success) return;

    if (!barData || !barData.dates || barData.dates.length === 0) return;

    const targetDate = dialogState.studyStartDate;
    if (!targetDate) {
      alert('Please select a study start date.');
      return;
    }

    // Find the nearest trading date index at or before the target date
    let cutoffIdx = -1;
    for (let i = 0; i < barData.dates.length; i++) {
      const d = barData.dates[i].substring(0, 10);
      if (d <= targetDate) cutoffIdx = i;
      else break;
    }
    if (cutoffIdx < 0) {
      alert('No trading dates found at or before the selected date.');
      return;
    }
    const cutoffDate = barData.dates[cutoffIdx].substring(0, 10);

    setStudyVisibleCount(cutoffIdx + 1);
    setStudyMode(true);
    setStatusText(`Animate mode: starting at ${cutoffDate}...`);
    setIsAstroGraphDialogOpen(false);

    // Deactivate area zoom
    try {
      const chart = eChartRef.current?.getEchartsInstance?.();
      if (chart) {
        chart.dispatchAction({ type: 'takeGlobalCursor', key: 'dataZoomSelect', dataZoomSelectActive: false });
      }
    } catch (_) { /* ignore */ }

    // Clear any existing animate interval
    if (animateIntervalRef.current) {
      clearInterval(animateIntervalRef.current);
      animateIntervalRef.current = null;
    }

    // --- Imperative animation: bypass React state to avoid full re-render cascade ---
    // We patch the chart directly each tick. NO setStudyVisibleCount during animation.
    // IMPORTANT: barData may have duplicate dates (e.g. 51277 entries) but the chart's
    // xAxis uses deduped dates (e.g. 8373 entries). We must use the deduped arrays
    // so the data dimensions match the xAxis categories.
    const dedupedData = extendBarData(barData, 0);
    const totalBars = dedupedData.dates.length;
    const ohlcFull = dedupedData.ohlc;
    const volumesFull = dedupedData.volumes;
    // Find cutoff in deduped data (original cutoffIdx may differ due to dedup)
    let dedupCutoffIdx = -1;
    for (let i = 0; i < dedupedData.dates.length; i++) {
      const d = dedupedData.dates[i].substring(0, 10);
      if (d <= targetDate) dedupCutoffIdx = i;
      else break;
    }
    if (dedupCutoffIdx < 0) dedupCutoffIdx = 0;
    let currentCount = dedupCutoffIdx + 1;
    const delaySec = dialogState.animateDelaySec || 1;

    animatePausedRef.current = false;
    animateIntervalRef.current = setInterval(() => {
      if (animatePausedRef.current) return; // skip tick while paused
      currentCount++;
      if (currentCount > totalBars) {
        // Animation complete — sync React state and exit
        clearInterval(animateIntervalRef.current);
        animateIntervalRef.current = null;
        setStudyMode(false);
        setStudyVisibleCount(0);
        setStatusText('Study mode ended.');
        return;
      }
      // Patch chart directly — no React re-render
      // Include type + axis indices to prevent ECharts merge from resetting axis bindings
      const instance = eChartRef.current?.getEchartsInstance?.();
      if (instance) {
        const visibleOhlc = ohlcFull.slice(0, currentCount);
        const visibleVols = volumesFull.slice(0, currentCount);
        instance.setOption({
          series: [
            { id: '__candle__', type: 'candlestick', xAxisIndex: 0, yAxisIndex: 0, data: visibleOhlc, animation: false },
            { id: '__volume__', type: 'bar', xAxisIndex: 1, yAxisIndex: 2, data: visibleVols, animation: false },
          ]
        }, false);
      }
    }, delaySec * 1000);
  }, [handleAstroGraphExecute, barData]);

  const handleRunAstroGraphPatterns = useCallback(async () => {
    // Find the AstroGraph overlay
    const agOverlay = overlays.find(o => o.type === 'astroGraphLine');
    if (!agOverlay || !agOverlay.data || !barData) {
      alert('No TrendPulse overlay found. Run TrendPulse first.');
      return;
    }
    const priceCloses = (barData?.ohlc || []).map(bar => bar[3]);
    const priceDates  = (barData?.dates || []).map(d => d.substring(0, 10));
    const payload = {
      series: agOverlay.data,
      priceCloses,
      priceDates,
    };
    try {
      console.log('[App.js] /api/astrograph-patterns payload size:', payload.series.length);
      const response = await api.post('/api/astrograph-patterns', payload, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const { patterns, summary, error: backendError } = response.data;
      if (backendError) {
        alert('Pattern detection error: ' + backendError);
        return;
      }
      console.log('[App.js] Pattern detection result:', { patternCount: patterns?.length, summary });
      // Remove any existing pattern overlay before adding new one
      const existingPatternOverlay = overlays.find(o => o.type === 'astroPatternLines');
      if (existingPatternOverlay) {
        removeOverlay(existingPatternOverlay.id);
      }
      addOverlay({
        id:       `astro-patterns-${Date.now()}`,
        type:     'astroPatternLines',
        label:    'TrendPulse Patterns',
        patterns: patterns || [],
        summary:  summary || {},
      });
    } catch (err) {
      console.error('[App.js] /api/astrograph-patterns error:', err);
      alert(`Pattern detection failed: ${err.response?.data?.error || err.message}`);
    }
  }, [api, token, barData, overlays, addOverlay, removeOverlay]);

  // Line -> Candlestick -> (admin only) Hidden -> Line. Standard users keep the two-state toggle;
  // the cycle itself enforces that, so this handler does not need to know about entitlements.
  const handleToggleChartType = useCallback(() => {
    setChartType(prevType => nextChartView(prevType, isAdmin));
  }, [isAdmin]);

  // What the chart and the toolbar actually use. Rendering goes through the normaliser rather
  // than through raw state so that losing admin while the chart is hidden — a token refresh, or
  // signing back in as a standard user — restores a visible chart immediately, instead of leaving
  // a blank canvas and a two-state cycle that never gets back to it.
  const effectiveChartType = useMemo(
    () => normalizeChartView(chartType, isAdmin),
    [chartType, isAdmin]
  );

  // Watches the backend's data-freshness revision. The poll is cheap - a public endpoint
  // returning a counter - and the callback only fires when that counter actually moves, so
  // the steady state costs one small request every couple of minutes and nothing else.
  useMarketDataSync(api, {
    market: appConfig?.market === 'IN' ? 'IN' : 'US',
    onSyncComplete: useCallback((event) => {
      console.info(
        '[App.js] market data sync completed',
        event ? `(${event.market}: ${event.updated} ticker(s) updated, latest ${event.latestDataDate})` : ''
      );
      setMarketDataRevision(r => r + 1);
    }, []),
  });

  // Which symbol the chart currently holds data for. Comparing against it is what separates a
  // fetch the customer asked for from one the app decided to do on its own.
  const loadedSymbolRef = useRef(null);

  useEffect(() => {
    // Only a first load or a symbol change blanks the canvas. A background refresh keeps the
    // existing chart on screen and swaps the data in underneath when it arrives.
    //
    // This is what caused "Loading chart data..." to appear unprovoked. The effect re-runs
    // whenever `api` changes identity, and `api` is a useMemo over the Firebase ID token — which
    // Firebase rotates roughly hourly. So a chart nobody had touched would blank itself and
    // reload, mid-session, for a token refresh that had nothing to do with the data. The same
    // happened when the sync poll reported a change. Neither is a reason to take the chart away
    // from someone who is reading it.
    const isBackgroundRefresh = loadedSymbolRef.current === selectedSymbol;

    const fetchData = async () => {
      if (!isBackgroundRefresh) {
        setLoading(true);
        setError(null);
      }
      try {
        // Add aggressive cache-busting with timestamp and random parameter
        const timestamp = Date.now();
        const random = Math.random();
        const res = await apiRef.current.get(`/api/getDataForSymbol/${selectedSymbol}?_t=${timestamp}&_r=${random}`);
        const rawData = res.data;
        if (!Array.isArray(rawData)) throw new Error('Data is not an array');

        const sortedData = rawData.sort((a, b) => new Date(a.date) - new Date(b.date));

        const formattedData = {
          symbol: selectedSymbol,
          dates: sortedData.map(item => item.date),
          ohlc: sortedData.map(item => [item.open, item.close, item.low, item.high]),
          volumes: sortedData.map(item => item.volume),
        };

        setBarData(formattedData);
        loadedSymbolRef.current = selectedSymbol;
      } catch (err) {
        // A background refresh that fails leaves the chart exactly as it was. Replacing a chart
        // the customer is reading with an error banner, because a refresh they never asked for
        // failed, is strictly worse than showing slightly older data.
        if (isBackgroundRefresh) {
          console.warn('[App.js] background chart refresh failed; keeping existing data:', err.message);
        } else {
          setError(err.message);
        }
      } finally {
        if (!isBackgroundRefresh) setLoading(false);
      }
    };
    fetchData();
    // marketDataRevision is deliberately a dependency: incrementing it after a completed
    // sync is what re-fetches the chart without the customer refreshing the page.
    //
    // `api` deliberately is NOT one. It is read through a ref, so a token refresh no longer
    // re-runs this effect at all — the token changing says nothing about the data having changed.
  }, [selectedSymbol, marketDataRevision]);

  /**
   * Run an Elliott wave analysis on what the chart is currently showing.
   *
   * The panel opens immediately, before the request resolves, so the click has a visible effect
   * even on a slow connection. Opening it only on success would leave the toolbar spinner as the
   * sole feedback and make a failure look like nothing happened at all.
   */
  const handleRunElliottAnalysis = useCallback(async () => {
    const source = barData && Array.isArray(barData.dates) && barData.dates.length > 0
      ? barData
      : extendedBarData;
    const body = buildElliottRequest(source, {
      symbol: selectedSymbol || source?.symbol,
      timeframe: 'Daily',
    });

    setElliottError(null);
    setElliottResult(null);
    setElliottOpen(true);

    // Checked here as well as server-side: a round trip that can only come back "too few bars" is
    // one the customer waits for to be told something already known.
    if (body.bars.length < MIN_BARS_FOR_ANALYSIS) {
      setElliottError(`This chart has ${body.bars.length} usable bars. `
        + `At least ${MIN_BARS_FOR_ANALYSIS} are needed to detect swings — load a longer range.`);
      return;
    }

    setElliottLoading(true);
    try {
      const res = await api.post('/api/charts/elliott-analysis', body);
      setElliottResult(res.data);
      // A stale polyline from the previous symbol or range would sit on the new chart looking like
      // a count of it. Cleared on every run; the switch re-draws from the new result.
      removeOverlay(ELLIOTT_OVERLAY_ID);
      setElliottOverlayShown(false);
    } catch (err) {
      console.error('[App] Elliott analysis failed:', err);
      setElliottError(err.response?.data?.message || err.message || 'The analysis request failed.');
    } finally {
      setElliottLoading(false);
    }
  }, [api, barData, extendedBarData, selectedSymbol, removeOverlay]);

  /**
   * Draw or clear the wave polyline on the price panel.
   *
   * Goes through the overlay system rather than patching the chart instance directly. An
   * imperative patch would survive only until the next full option rebuild — which any other
   * overlay change triggers — and the wave lines would then vanish for no reason the customer
   * could see. The candlestick data itself is untouched either way; nothing is refetched and no
   * analysis is re-run.
   */
  const handleToggleElliottOverlay = useCallback((show) => {
    if (!show) {
      removeOverlay(ELLIOTT_OVERLAY_ID);
      setElliottOverlayShown(false);
      return;
    }
    const overlay = buildElliottOverlay(elliottResult);
    if (!overlay) {
      // The switch is disabled in this case, so reaching here means the result changed underneath
      // it. Leaving the switch off is the honest outcome; turning it on would claim a drawing that
      // is not there.
      setElliottOverlayShown(false);
      return;
    }
    // Replace rather than stack: re-running the analysis must not leave the previous count behind.
    removeOverlay(ELLIOTT_OVERLAY_ID);
    addOverlay(overlay);
    setElliottOverlayShown(true);
  }, [addOverlay, removeOverlay, elliottResult]);

  const handleRunSingleStudy = useCallback(async (studyOrOverlays) => {
    setLoading(true);
    setError(null);

    try {
      if (Array.isArray(studyOrOverlays)) {
        studyOrOverlays.forEach(addOverlay);
        setLoading(false);
        return;
      }

      const study = studyOrOverlays;
      if (!study.type) study.type = 'longitudinalDistance';

      const longitudeTypes = ['longitudinalDistance', 'longitudeFollow', 'sidereal', 'declination', 'longitude', 'longitudeDifference', 'planetFollow'];
      if (longitudeTypes.includes(study.type)) {
        if (!barData || !barData.dates || barData.dates.length === 0) {
          alert('Chart data is not available. Please load a symbol first.');
          return;
        }

        const fromDate = extendedBarData.dates[0];
        const toDate = extendedBarData.dates[extendedBarData.dates.length - 1];

        // frame decides whether the backend computes heliocentric or geocentric longitudes. Defaulted
        // here rather than left undefined, so a study object without one is explicit about asking
        // for the conventional frame instead of relying on the endpoint's own default.
        const params = new URLSearchParams({
          planet1: study.planet1, planet2: study.planet2, degrees: study.degrees, fromDate, toDate,
          frame: study.frameOfReference === 'Heliocentric' ? 'heliocentric' : 'geocentric',
        });

        const response = await api.get(`/api/followstudy/dates?${params.toString()}`);
        const dates = response.data;
        const overlaysToAdd = dates.map(date => {
          const formattedDate = date.split('T')[0];
          return {
            type: 'verticalLine',
            date: formattedDate,
            label: `${study.planet1.substring(0, 2)}-${study.planet2.substring(0, 2)} ${study.degrees}°\n${formattedDate}`,
            color: study.color || '#000000',
            thickness: study.thickness || 2,
            planet1: study.planet1,
            planet2: study.planet2,
            degrees: study.degrees,
          };
        });

        overlaysToAdd.forEach(addOverlay);
      } else {
        console.log(`Study type ${study.type} not handled yet.`);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [barData, extendedBarData, addOverlay, api]);

  const handleRunPlanetaryLines = useCallback(async ({ planet, color, lineType, thickness, mode, pricePer360, drawRawLongitude, autoFitOctaves, octaveStart, octaveEnd }) => {
    if (!extendedBarData || !extendedBarData.dates || !extendedBarData.dates.length) {
      alert('Chart data unavailable');
      return;
    }

    const panel = 'main'; // Force main panel as per Dialog logic
    const dates = extendedBarData.dates;
    const isLongitude = mode === 'longitude';

    // Log activity
    const eventDetails = {
      studyType: 'planetaryLine',
      parameters: { planet, pricePer360: panel === 'main' ? pricePer360 : undefined }
    };
    await logActivity(api, user, 'RUN_STUDY', eventDetails);

    const payload = {
      planet,
      dates,
      ...(panel === 'main' ? { pricePer360 } : {}),
      drawRawLongitude,
      isLongitude
    };

    try {
      const res = await api.post('/api/followstudy/getPlanetaryLines', payload);
      const data = res.data;
      
      if (Array.isArray(data.longitudes) && data.longitudes.length === dates.length) {
         const rawDegrees = data.longitudes;
         const degreesForPlot = (mode === 'declination' || drawRawLongitude) ? rawDegrees : unwrapDegrees(rawDegrees);

         let overlayData;
         if (panel === 'main') {
           if (mode === 'declination') {
             overlayData = dates.map((date, i) => ({ value: [date, degreesForPlot[i]] }));
           } else {
             const price360 = parseFloat(pricePer360);
             const price360Safe = Number.isFinite(price360) ? price360 : 360;
             overlayData = dates.map((date, i) => ({ value: [date, (degreesForPlot[i] * price360Safe) / 360] }));
           }
         } else {
             overlayData = dates.map((date, i) => ({ value: [date, degreesForPlot[i]] }));
         }

         addOverlay({
            id: `planetary-${planet}-${Date.now()}`,
            type: 'planetaryLine',
            mode,
            planet,
            planet2: planet,
            color,
            thickness,
            lineType,
            panel,
            pricePer360: (panel === 'main' ? (Number.isFinite(parseFloat(pricePer360)) ? pricePer360 : '360') : undefined),
            // Octave coverage, read by chartDataProcessor. Undefined on an overlay saved before
            // these existed, which octavesFor treats as auto-fit — the behaviour it always had.
            autoFitOctaves,
            octaveStart,
            octaveEnd,
            data: overlayData
         });

      } else {
         alert('Planet longitude data length mismatch.');
      }

    } catch (err) {
      console.error('[App.js] Error fetching planetary lines:', err);
      alert(`Error: ${err.message}`);
    }

  }, [extendedBarData, addOverlay, api, user]);

  const handleRunSeasonality = useCallback(async ({ tradingDays, matchMode, displayType, asOfDate }) => {
    if (!barData || !barData.ohlc || !barData.ohlc.length) {
      alert('Chart data unavailable');
      return;
    }
    const symbol = barData.symbol || selectedSymbol;
    const chartLastDate = barData.dates[barData.dates.length - 1]?.split('T')[0];
    const lastBarDate = asOfDate || chartLastDate;

    // Find the close price for the as-of date
    let currentPrice;
    if (asOfDate && asOfDate !== chartLastDate) {
      const targetDate = asOfDate.split('T')[0];
      const idx = barData.dates.findIndex(d => d.split('T')[0] === targetDate);
      if (idx !== -1) {
        currentPrice = barData.ohlc[idx][1]; // close price
      } else {
        // Find nearest prior date
        for (let i = barData.dates.length - 1; i >= 0; i--) {
          if (barData.dates[i].split('T')[0] <= targetDate) {
            currentPrice = barData.ohlc[i][1];
            break;
          }
        }
      }
    }
    if (currentPrice === undefined) {
      currentPrice = barData.ohlc[barData.ohlc.length - 1][1];
    }

    try {
      const res = await api.post('/api/followstudy/seasonality-projection', {
        symbol,
        tradingDays,
        matchMode,
        currentPrice,
        lastBarDate,
      });
      const data = res.data;
      if (data && Array.isArray(data.projections) && data.projections.length > 0) {
        // Remove any previous seasonality overlay before adding new one
        const existing = overlays.filter(o => o.type === 'seasonalityProjection');
        existing.forEach(o => removeOverlay(o.id));

        addOverlay({
          id: 'seasonality-projection',
          type: 'seasonalityProjection',
          displayType: displayType || 'candlestick',
          projections: data.projections,
        });
        setStatusText(`Seasonality projection: ${data.projections.length} days`);
      } else {
        alert('No seasonality data returned');
      }
    } catch (err) {
      console.error('[App.js] Seasonality projection error:', err);
      alert(`Seasonality error: ${err.message}`);
    }
  }, [barData, selectedSymbol, overlays, addOverlay, removeOverlay, api]);

  const handleRunCharacteristicsPlot = useCallback(async ({ characteristicType, planet1, planet2, color, lineThickness, lineType, panel }) => {
    if (!extendedBarData || !Array.isArray(extendedBarData.dates) || extendedBarData.dates.length === 0) {
      alert('Chart data unavailable');
      return;
    }
    try {
      const payload = {
        characteristicType,
        planet1,
        planet2,
        dates: extendedBarData.dates,
      };
      const res = await api.post('/api/followstudy/characteristics-series', payload);
      const data = res.data;
      if (data?.error) {
        alert('Characteristics error: ' + data.error);
        return;
      }
      const values = Array.isArray(data?.values) ? data.values : [];
      if (values.length !== extendedBarData.dates.length) {
        alert('Characteristics: data length mismatch.');
        return;
      }

      const labelBase =
        characteristicType === 'longitude_diff' ? `Lon diff: ${planet1}-${planet2}`
        : characteristicType === 'declination_diff' ? `Decl diff: ${planet1}-${planet2}`
        : characteristicType === 'distance_percent_diff' ? `Dist% diff: ${planet1}-${planet2}`
        : characteristicType === 'distance_percent' ? `Dist%: ${planet1}`
        : characteristicType === 'speed' ? `Speed: ${planet1}`
        : `${characteristicType}`;

      const overlayData = extendedBarData.dates.map((d, i) => ({ value: [d, values[i]] }));

      addOverlay({
        id: `characteristic-${characteristicType}-${planet1}-${planet2 || ''}-${Date.now()}`,
        type: 'characteristicLine',
        characteristicType,
        planet1,
        planet2,
        label: labelBase,
        color: color || '#E67C73',
        thickness: String(lineThickness || '2'),
        lineType: (lineType || 'Solid').toLowerCase(),
        panel: panel || 'main',
        data: overlayData,
      });
    } catch (err) {
      console.error('[App.js] Characteristics plot error:', err);
      alert(`Characteristics failed: ${err.response?.data?.error || err.message}`);
    }
  }, [extendedBarData, api, addOverlay]);

  const handleOpenAreaHighlightDialog = () => setIsAreaHighlightDialogOpen(true);
  const handleCloseAreaHighlightDialog = () => setIsAreaHighlightDialogOpen(false);

  const [statusText, setStatusText] = useState('Ready');

  useEffect(() => {
    if (statusText !== 'Ready') {
      const timer = setTimeout(() => {
        setStatusText('Ready');
      }, 3000);
      return () => clearTimeout(timer); // Cleanup the timer
    }
  }, [statusText]);

  const handleBarSelection = useCallback((date, dataIndex, price) => {

    // ── Drawing tool modes ──
    if (drawingMode) {
      // ---- Technical tools: Fibonacci (2 clicks), extended trendline and pitchfork (3) ----
      //
      // All three compute in (barIndex, price) space and emit ordinary `trendline` overlays, one
      // per line, sharing a groupId. Reusing the proven two-point renderer means no new drawing
      // path to get wrong; the groupId is what lets a whole figure be identified as one shape.
      //
      // They carry their own `source`, NOT MANUAL_DRAW_SOURCE, so construction geometry never
      // picks up a measurement badge or a line in the journal's move summary — a Fibonacci level
      // is not a trade anyone took.
      if (TOOL_CLICKS[drawingMode]) {
        const needed = TOOL_CLICKS[drawingMode];
        drawingPoints.current = [...drawingPoints.current, { index: dataIndex, price, date }];
        const pts = drawingPoints.current;

        if (pts.length < needed) {
          setStatusText(`${drawingMode.replace('draw_', '').replace(/_/g, ' ')}: point ${pts.length} of ${needed} set. Click ${needed - pts.length} more.`);
          return;
        }
        drawingPoints.current = [];
        setDrawingMode(null);

        // Projected against the EXTENDED dates, which is what the chart actually renders and what
        // the click's dataIndex is an index into. Using barData here was the bug behind pitchfork
        // rays stopping dead at the last price bar: clampIndex pinned every projection to the last
        // REAL bar, so the forks could never reach the ~250 padded future bars already on screen.
        // Real bars occupy the same indices in both arrays — padding is appended — so nothing else
        // shifts.
        const dates = extendedBarData?.dates?.length ? extendedBarData.dates : (barData?.dates || []);
        const lastIndex = dates.length - 1;

        // How far forward a projection may run. Bounded rather than run to the end of the padding:
        // a fork extended 250 bars past the data is mostly empty space, and its rays fan so wide
        // that the part near the pivots — the part being read — is squeezed flat.
        const lastRealIndex = Math.max(0, (barData?.dates?.length || dates.length) - 1);
        const projectionEnd = Math.min(lastIndex, lastRealIndex + FUTURE_PROJECTION_BARS);

        const dateAt = i => dates[clampIndex(i, lastIndex)];
        const groupId = `${drawingMode}-${Date.now()}`;
        const line = (from, to, extra = {}) => addDrawing({
          type: 'trendline',
          date1: dateAt(from.index), price1: from.price,
          date2: dateAt(to.index), price2: to.price,
          color: activeDrawingColor,
          source: TECHNICAL_DRAW_SOURCE,
          groupId,
          id: `${groupId}-${extra.part || 'line'}`,
          ...extra,
        });

        if (drawingMode === 'draw_fibonacci') {
          const [p1, p2] = pts;
          line(p1, p2, { part: 'trend' });   // the move being retraced

          // Segments come from one function so the 0%/100% boundaries and the interior levels
          // share an X-span by construction rather than by two call sites agreeing.
          const segments = fibonacciSegments(p1, p2);
          segments.forEach(seg => line(seg.from, seg.to, {
            part: seg.part,
            lineType: seg.lineType,
            lineWidth: seg.lineWidth,
            endLabel: seg.label,
          }));
          setStatusText(`Fibonacci retracement drawn (${segments.length} levels).`);
          return;
        }

        if (drawingMode === 'draw_fib_time') {
          const [q1, q2] = pts;
          // Projected against `dates`, which is the EXTENDED array here, so targets can land in
          // the padded future space rather than being clamped to the last price bar.
          const overlays = buildFibTimeOverlays(q1, q2, dates, {
            color: activeDrawingColor,
            groupId,
            lastRealIndex,
          });
          if (overlays.length === 0) {
            setStatusText('Fib Time: those two points give no interval to project.');
            return;
          }
          // addOverlay, not addDrawing: addDrawing takes ONE overlay and spreads it, so an array
          // would become {0:..., 1:...}. The context flattens arrays, and every line already
          // carries the shared groupId, so they select, delete and undo as a single figure.
          addOverlay(overlays);
          const future = overlays.filter(o => o.isFuture).length;
          setStatusText(`Fib Time: ${overlays.length - 2} projection(s) from ${dateAt(q2.index)}`
            + `${future ? `, ${future} in future space` : ''}. Interval ${q2.index - q1.index} bars.`);
          return;
        }

        if (drawingMode === 'draw_extended_trendline') {
          const [p1, p2, p3] = pts;
          const end = extendedTrendlineEnd(p1, p2, p3);
          if (!end) { setStatusText('Extended trendline needs two points on different bars.'); return; }
          line(p1, end, { part: 'extended' });
          setStatusText(`Trendline extended to ${dateAt(end.index)} (${end.price.toFixed(2)}).`);
          return;
        }

        if (drawingMode === 'draw_pitchfork') {
          const [p1, p2, p3] = pts;
          const fork = pitchfork(p1, p2, p3, projectionEnd);
          if (!fork) { setStatusText('Pitchfork needs three pivots that define a direction.'); return; }
          line(fork.median.from, fork.median.to, { part: 'median', lineWidth: 2 });
          line(fork.upper.from, fork.upper.to, { part: 'upper', lineType: 'dashed' });
          line(fork.lower.from, fork.lower.to, { part: 'lower', lineType: 'dashed' });
          line(fork.base.from, fork.base.to, { part: 'base' });
          setStatusText('Andrews’ pitchfork drawn.');
          return;
        }
      }

      // Vertical and horizontal SEGMENTS, not full-span lines.
      //
      // Both take two clicks, and the second click contributes only ONE of its coordinates: it
      // acts as a boundary line rather than as an endpoint. For a vertical segment the second
      // click supplies its price via an imaginary horizontal line through it, and its date is
      // discarded; for a horizontal segment the reverse. That is what lets a segment be bounded
      // by a point that is nowhere near the line itself.
      //
      // Emitted as `trendline`, which already renders a 2-point series between (date1, price1)
      // and (date2, price2) — a segment with one coordinate held constant is exactly that, so
      // there is no new rendering path to write or keep working. `tool` records which drawing
      // tool produced it, since the geometry alone no longer says.
      if (drawingMode === 'draw_vertical_line') {
        if (!drawingFirstClick.current) {
          drawingFirstClick.current = { date, price, dataIndex };
          setStatusText(`Vertical segment: start set at ${date}. Click to set how far it extends.`);
          return;
        }
        const first = drawingFirstClick.current;
        addDrawing({
          type: 'trendline',
          tool: 'verticalSegment',
          date1: first.date,
          price1: first.price,
          date2: first.date,          // X held at the first click
          price2: price,              // Y bounded by the second click
          color: activeDrawingColor,
          source: MANUAL_DRAW_SOURCE,
          id: `draw-vseg-${Date.now()}`
        });
        setDrawingMode(null);
        drawingFirstClick.current = null;
        setStatusText(`Vertical segment drawn at ${first.date}`);
        return;
      }
      if (drawingMode === 'draw_horizontal_line') {
        if (!drawingFirstClick.current) {
          drawingFirstClick.current = { date, price, dataIndex };
          setStatusText(`Horizontal segment: start set at ${Math.round(price * 100) / 100}. Click to set how far it extends.`);
          return;
        }
        const first = drawingFirstClick.current;
        addDrawing({
          type: 'trendline',
          tool: 'horizontalSegment',
          date1: first.date,
          price1: first.price,
          date2: date,                // X bounded by the second click
          price2: first.price,        // Y held at the first click
          color: activeDrawingColor,
          source: MANUAL_DRAW_SOURCE,
          id: `draw-hseg-${Date.now()}`
        });
        setDrawingMode(null);
        drawingFirstClick.current = null;
        setStatusText(`Horizontal segment drawn at ${Math.round(first.price * 100) / 100}`);
        return;
      }
      if (drawingMode === 'draw_fixed_cycle') {
        const cycleDates = extendedBarData?.dates?.length ? extendedBarData.dates : (barData?.dates || []);
        const lastReal = Math.max(0, (barData?.dates?.length || cycleDates.length) - 1);
        const overlays = buildFixedCycleOverlays(dataIndex, fixedCycleLength, cycleDates, {
          color: activeDrawingColor,
          groupId: `fixedcycle-${dataIndex}-${fixedCycleLength}-${Date.now()}`,
          lastRealIndex: lastReal,
          unit: fixedCycleUnit,
        });
        if (overlays.length === 0) {
          setStatusText('Fixed cycle: could not anchor there.');
          return;
        }
        // Replacing rather than stacking: two cycles of the same length on one chart is the same
        // statement twice, and the second would be indistinguishable from the first.
        setAllOverlays([...withoutFixedCycles(overlaysRef.current), ...overlays]);
        setDrawingMode(null);
        drawingFirstClick.current = null;
        const forward = overlays.filter(o => o.cycleK > 0).length;
        const back = overlays.filter(o => o.cycleK < 0).length;
        setStatusText(`Fixed cycle of ${fixedCycleLength} `
          + `${fixedCycleUnit === 'days' ? 'calendar day(s)' : 'bar(s)'} anchored at ${date}: `
          + `${back} back, ${forward} forward.`);
        return;
      }

      if (drawingMode === 'draw_vertical_full') {
        // A full-height marker, not a measured move: one click, spanning the whole pane.
        //
        // Emitted as `vLine`, which renders as a markLine on the x-axis and therefore always
        // reaches top to bottom whatever the price scale does. Deliberately NOT a `trendline`
        // with equal endpoints: the measurement badges are attached to manually drawn trendlines,
        // so that shape would earn a "+0.00 pts (+0.00%)" callout on a line that measures nothing.
        addDrawing({
          type: 'vLine',
          date,
          color: activeDrawingColor,
          thickness: manualDrawStyle?.lineWidth || 2,
          label: '',
          id: `draw-vfull-${Date.now()}`,
        });
        setDrawingMode(null);
        drawingFirstClick.current = null;
        setStatusText(`Vertical line drawn at ${date}`);
        return;
      }
      if (drawingMode === 'draw_circle_empty' || drawingMode === 'draw_circle_filled') {
        const filled = drawingMode === 'draw_circle_filled';
        addDrawing({
          type: 'pivotMarkers',
          // Reuses the scatter renderer the pivot studies already use, so there is no new drawing
          // path to get wrong — only the symbol and size differ.
          symbol: filled ? 'circle' : 'emptyCircle',
          symbolSize: activeTextSize.px,
          color: activeDrawingColor,
          label: filled ? 'Filled circle' : 'Circle',
          points: [{ date, price, color: activeDrawingColor }],
          id: `draw-circle-${filled ? 'filled' : 'empty'}-${Date.now()}`,
        });
        setDrawingMode(null);
        drawingFirstClick.current = null;
        setStatusText(`${filled ? 'Filled' : 'Empty'} circle placed at ${date} (${activeTextSize.label}).`);
        return;
      }
      if (drawingMode === 'draw_trendline') {
        if (!drawingFirstClick.current) {
          drawingFirstClick.current = { date, price, dataIndex };
          setStatusText(`Trendline: first point set at ${date}. Click second point.`);
          return;
        }
        const first = drawingFirstClick.current;
        addDrawing({
          type: 'trendline',
          date1: first.date,
          price1: first.price,
          date2: date,
          price2: price,
          color: activeDrawingColor,
          source: MANUAL_DRAW_SOURCE,
          id: `draw-trend-${Date.now()}`
        });
        setDrawingMode(null);
        drawingFirstClick.current = null;
        setStatusText(`Trendline drawn from ${first.date} to ${date}`);
        return;
      }
      if (drawingMode === 'draw_rectangle') {
        if (!drawingFirstClick.current) {
          drawingFirstClick.current = { date, price, dataIndex };
          setStatusText(`Rectangle: first corner set at ${date}. Click opposite corner.`);
          return;
        }
        const first = drawingFirstClick.current;
        addDrawing({
          type: 'drawRect',
          date1: first.date,
          price1: first.price,
          date2: date,
          price2: price,
          // Fill and border cannot be the same value: a rectangle filled at full opacity would
          // hide the candles it is drawn to frame.
          color: withAlpha(activeDrawingColor, 0.18),
          borderColor: activeDrawingColor,
          id: `draw-rect-${Date.now()}`
        });
        setDrawingMode(null);
        drawingFirstClick.current = null;
        setStatusText(`Rectangle drawn from ${first.date} to ${date}`);
        return;
      }
      if (drawingMode === 'draw_text') {
        addDrawing({
          type: 'textAnnotation',
          date,
          price,
          dataIndex,
          text: 'Note',
          color: activeDrawingColor,
          // Stored per note rather than read from current state at render time, so changing the
          // tool size later does not silently resize every annotation already on the chart.
          fontSize: activeTextSize.px,
          id: `draw-text-${Date.now()}`
        });
        setDrawingMode(null);
        drawingFirstClick.current = null;
        setStatusText(`Text annotation placed at ${date}. Double-click to edit.`);
        return;
      }
    }

    if (isManualDrawMode) {
      addOverlay({
        type: 'verticalLine',
        source: 'manualCycle',
        date,
        color: manualDrawStyle.color,
        thickness: manualDrawStyle.lineWidth,
        id: `manual-cycle-${Date.now()}`
      });
      setStatusText(`Added manual cycle line at ${date}`);
      // Do not return here; allow bar selection to proceed
    }

    if ((nextBarToSelect === 'bar1' && date === bar2Date) || (nextBarToSelect === 'bar2' && date === bar1Date)) {
      setStatusText('Cannot select the same bar for both start and end points.');
      return;
    }

    if (nextBarToSelect === 'bar1') {
      setBar1Date(date);
      setNextBarToSelect('bar2');
      setStatusText(`Bar 1 set to ${date} (Bar #${dataIndex + 1}). Now select Bar 2.`);
    } else {
      const firstDate = bar1Date;
      const secondDate = date;

      if (new Date(secondDate) < new Date(firstDate)) {
        setBar1Date(secondDate);
        setBar2Date(firstDate);
        setStatusText(`Dates set: ${secondDate} and ${firstDate}. Swapped for chronological order.`);
      } else {
        setBar2Date(secondDate);
        setStatusText(`Dates set: ${firstDate} and ${secondDate}.`);
      }
      
      setNextBarToSelect('bar1');
    }
    // Deliberately omits activeTextSize and the two bar-data arrays. This callback is handed to
    // EChartComponent as onBarSelect, and that component states the constraint plainly: a handler
    // recreated each render makes echarts-for-react dispose and rebuild the whole chart, which
    // shows as flicker. Rebuilding it whenever price data arrives is the same fault the
    // overlaysRef above exists to avoid — the values are read when the handler fires, which is
    // after any of them could have changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addOverlay, addDrawing, setAllOverlays, activeDrawingColor, bar1Date, bar2Date, drawingMode, fixedCycleLength, fixedCycleUnit, isManualDrawMode, manualDrawStyle, nextBarToSelect]);


  const handleDisplayCycles = (dialogState) => {
    if (bar1Date && bar2Date) {
      addOverlay({
        type: 'cycleLines',
        ...dialogState,
        bar1Date,
        bar2Date,
        id: `cycle-${Date.now()}` // Unique ID for the new overlay
      });
    }
  };

  const handleEraseCycles = () => {
    // Filter out both calculated and manual cycle lines
    const newOverlays = overlays.filter(o => o.type !== 'cycleLines' && o.source !== 'manualCycle');
    setAllOverlays(newOverlays);
  };

  const handleManualDrawToggle = (isEnabled, style) => {
    setIsManualDrawMode(isEnabled);
    if (isEnabled) {
      setManualDrawStyle(style);
    }
  };

  const handleZoomRangeChange = useCallback((startBar, endBar) => {
    const minBar = Math.min(startBar, endBar) + 1;
    const maxBar = Math.max(startBar, endBar) + 1;
    setStatusText(`Zoomed bars: ${minBar} to ${maxBar}`);
  }, []);

  const handleTriggerAreaZoom = () => {
    if (eChartRef.current) {
      const chart = eChartRef.current.getEchartsInstance();
      if (chart) {
        chart.dispatchAction({
          type: 'takeGlobalCursor',
          key: 'dataZoomSelect',
          dataZoomSelectActive: true
        });
      }
    }
  };


  return (
    <ThemeProvider theme={theme}>
      <HelpViewer />
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
    <AppBar position="static" color="primary" elevation={2} sx={{ zIndex: 1201 }}>
      <Toolbar>
        <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
          {`${watermarkText} - ${selectedSymbol}`}
        </Typography>
        <UserMenu />
    </Toolbar>
    </AppBar>
        {isTrial && !isExpired && (
          <Box sx={{ background: '#fff3e0', color: '#e65100', textAlign: 'center', py: 0.5, px: 2, fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
            <span>Trial account — you are viewing sample data. Expires on {user?.subscriptionEndDate ? new Date(user.subscriptionEndDate).toLocaleDateString() : 'N/A'}.</span>
          </Box>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, overflow: 'hidden' }}>
          <Paper elevation={2} sx={{ borderRadius: 0, borderBottom: '1px solid #ddd', zIndex: 10 }}>
            <TabPanel
               isAdmin={isAdmin}
               barData={barData}
               extendedBarData={extendedBarData}
               onOpenControlsDialog={() => setStudyDialogOpen(true)}
               showRSI={showRSI}
               onToggleRSI={() => setShowRSI((prev) => !prev)}
               showMACD={showMACD}
               onToggleMACD={() => setShowMACD((prev) => !prev)}
               onOpenPlanetFollowStudyDialog={() => setPlanetFollowStudyDialogOpen(true)}
               onOpenAspectsAreaHighlightDialog={() => setIsAspectsAreaHighlightDialogOpen(true)}
               onOpenDeclinationStudyDialog={() => setDeclinationStudyDialogOpen(true)}
               onOpenPlanetaryLinesDialog={() => setPlanetaryLinesDialogOpen(true)}
               onOpenVisualUniverseDialog={() => setVisualUniverseDialogOpen(true)}
               onOpenIntradayAnimationDialog={() => setIntradayAnimationDialogOpen(true)}
               onOpenChartComparisonDialog={() => setChartComparisonDialogOpen(true)}
               onOpenAreaHighlightDialog={handleOpenAreaHighlightDialog}
               onOpenAggregateAreaHighlightDialog={() => setIsAggregateAreaHighlightDialogOpen(true)}
               onOpenPlanetGroupDialog={() => setPlanetGroupDialogOpen(true)}
               onOpenStrategyReportStudyDialog={() => setStrategyReportStudyDialogOpen(true)}
               onOpenStrategyReportStudyBDialog={() => setStrategyReportStudyBDialogOpen(true)}
               onOpenStrategyDesignDialog={() => setIsStrategyDesignDialogOpen(true)}
               onOpenStrategy2DesignDialog={() => setIsStrategy2DesignDialogOpen(true)}
               onOpenTimeAndEventStrategyDialog={() => setTimeAndEventStrategyDialogOpen(true)}
               onOpenPlanetCharacteristicsDialog={() => setIsPlanetCharacteristicsDialogOpen(true)}
               onOpenAstroGraphDialog={() => setIsAstroGraphDialogOpen(true)}
               onRunAstroGraphPatterns={handleRunAstroGraphPatterns}
               onOpenPatternConfigDialog={() => setIsPatternConfigDialogOpen(true)}
               chartType={effectiveChartType}
               onToggleChartType={handleToggleChartType}
               messagesTabContents={messagesTabContents}
               setMessagesTabContents={setMessagesTabContents}
               messagesOpen={messagesOpen}
               setMessagesOpen={setMessagesOpen}
               messagesTabIndex={messagesTabIndex}
               onMessagesTabIndexChange={setMessagesTabIndex}
               onRunElliottAnalysis={handleRunElliottAnalysis}
               elliottLoading={elliottLoading}
               studyTitle={studyTitle}
               setStudyTitle={setStudyTitle}
               journalEditing={journalEditing}
               onJournalEditingChange={setJournalEditing}
               journalMarkdown={journalMarkdown}
               onJournalChange={setJournalMarkdown}
               onJournalReset={handleJournalReset}
               onJournalAppend={handleJournalAppend}
               onOpenHorizontalLineDialog={() => setSrDialogOpen(true)}
               onVerticalLinesAction={() => eChartRef.current?.toggleCycleMarkers?.()}
               onOpenCyclesDialog={() => setCyclesDialogOpen(true)}
               onTriggerAreaZoom={handleTriggerAreaZoom}
               studyMode={studyMode}
               onExitStudyMode={exitStudyMode}
               drawingMode={drawingMode}
               nextTextSize={textSizeAt(textSizeIndex)}
               onDeleteLastDrawing={handleDeleteLastDrawing}
               onRunPivotAdditionSeries={handleRunPivotAdditionSeries}
               onRunFibPivotPairs={handleRunFibPivotPairs}
               onToggleRecentFibProjections={handleToggleRecentFibProjections}
               onOpenFixedCycleDialog={() => setFixedCycleDialogOpen(true)}
               activeDrawingColor={activeDrawingColor}
               onDrawingColorChange={setActiveDrawingColor}
               barFloaterMode={barFloaterMode}
               onBarFloaterModeChange={setBarFloaterMode}
               onSetDrawingMode={(mode) => {
                 drawingFirstClick.current = null;
                 drawingPoints.current = [];
                 setDrawingMode(mode);
                 // Only an activation advances the cycle. Toggling the tool off passes null, and
                 // advancing on that would skip a size every time someone changed their mind.
                 if (isSizedTool(mode)) {
                   const { activeSize, nextIndex } = activateTextTool(textSizeIndex);
                   setActiveTextSize(activeSize);
                   setTextSizeIndex(nextIndex);
                   // Shared by the text and circle tools, so the message names whichever is active
                   // rather than always claiming to be the text tool.
                   const toolName = mode === 'draw_text' ? 'Text tool'
                     : mode === 'draw_circle_filled' ? 'Filled circle'
                     : 'Circle';
                   const extra = mode === 'draw_text' ? ' Click to place, double-click to edit.' : ' Click to place.';
                   setStatusText(`${toolName}: ${activeSize.label} (${activeSize.px}px).${extra} Click the tool again to change size.`);
                   return;
                 }
                 if (mode) setStatusText(`Drawing mode: ${mode.replace('draw_', '').replace(/_/g, ' ')}. Click on the chart. Esc to cancel.`);
               }}
               onToggleWatermark={handleToggleWatermark}
               watermarkOverride={watermarkOverride}
               watermarkText={watermarkText}
             />
            <HorizontalLineDialog
              open={srDialogOpen}
              onClose={() => setSrDialogOpen(false)}
              onRun={({ color, price, description }) => {
                if (typeof price === 'number' && !isNaN(price)) {
                                    addOverlay({
                    type: 'horizontalLine',
                    color,
                    price,
                    description: description || '',
                  });
                }
              }}
            />
          </Paper>
          <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}>
              <Box sx={{ flexGrow: 1, width: '100%', position: 'relative', display: 'flex', flexDirection: 'column' }}>
                {loading && <Typography>Loading chart data...</Typography>}
                {computingLabel && (
                  // Fixed, centred and above the chart, with pointer events captured so a second
                  // click cannot queue another multi-second scan behind the first.
                  <Box
                    sx={{
                      position: 'fixed', inset: 0, zIndex: 4000,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: 'rgba(0,0,0,0.08)', cursor: 'progress',
                    }}
                  >
                    <Box sx={{
                      display: 'flex', alignItems: 'center', gap: 1.5,
                      background: '#fff', borderRadius: 2, px: 3, py: 2,
                      boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
                    }}>
                      <CircularProgress size={22} />
                      <Box>
                        <Typography sx={{ fontWeight: 600 }}>{computingLabel}...</Typography>
                        <Typography variant="body2" color="text.secondary">
                          This can take a few seconds on a long chart.
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                )}
                {error && <Typography color="error">Error: {error}</Typography>}
                {extendedBarData && Array.isArray(extendedBarData.ohlc) && extendedBarData.ohlc.length > 0 && Array.isArray(extendedBarData.dates) && extendedBarData.dates.length > 0 ? (
                  <Box
                    sx={{ width: '100%', height: '100%' }}
                    onClickCapture={studyMode ? (e) => {
                      const instance = eChartRef.current?.getEchartsInstance?.();
                      if (!instance) return;
                      const rect = instance.getDom().getBoundingClientRect();
                      const ox = e.clientX - rect.left;
                      const oy = e.clientY - rect.top;
                      // Only intercept clicks inside the chart grid; let toolbox/slider clicks through
                      if (!instance.containPixel({ gridIndex: 0 }, [ox, oy])) return;
                      e.stopPropagation();
                      e.preventDefault();

                      // --- Animate mode: toggle pause/continue ---
                      if (animateIntervalRef.current) {
                        animatePausedRef.current = !animatePausedRef.current;
                        setStatusText(animatePausedRef.current ? 'Animation PAUSED — click to continue' : 'Animation resumed...');
                        return;
                      }

                      // --- Execute & Study mode: jump to clicked bar ---
                      try {
                        const dataIdx = instance.convertFromPixel({ gridIndex: 0 }, [ox, oy])[0];
                        if (typeof dataIdx === 'number' && dataIdx >= 0 && dataIdx + 1 > studyVisibleCount) {
                          setStudyVisibleCount(dataIdx + 1);
                        } else {
                          handleStudyAdvance();
                        }
                      } catch (_) {
                        handleStudyAdvance();
                      }
                    } : undefined}
                  >
                    <EChartComponent
                      ref={eChartRef}
                      barData={extendedBarData}
                      overlays={overlays}
                      showRSI={showRSI}
                      rsiData={rsiData}
                      showMACD={showMACD}
                      macdData={macdData}
                      theme={theme}
                      loading={loading}
                      error={error}
                      chartType={effectiveChartType}
                      onZoomRangeChange={handleZoomRangeChange}
                      maConfigs={maConfigs}
                      onBarSelect={handleBarSelection}
                      studyMode={studyMode}
                      barFloaterMode={barFloaterMode}
                      watermarkOverride={watermarkOverride}
                      watermarkText={watermarkText}
                      onUpdateOverlay={updateOverlay}
                    />
                  </Box>
                ) : (
                  <Box sx={{ width: '100%', textAlign: 'center', color: '#888', py: 6 }}>Loading chart data...</Box>
                )}
                <AreaHighlightDialog
                  open={isAreaHighlightDialogOpen}
                  onClose={handleCloseAreaHighlightDialog}
                  barData={extendedBarData}
                  onJournalAppend={handleJournalAppend}
                  onRun={(data) => {
                    const { title, summaryLines, futureDates } = data;
                    console.log('[MESSAGE_PANEL_DATA]', { title, summaryLines, futureDates });
                    setStudyTitle(title || '');
                    const tabs = formatMessagesPanelTabs(summaryLines, futureDates);
                    setMessagesTabContents(tabs);
                    setMessagesOpen(true);
                  }}
                />
              </Box>
            </Box>
            <SymbolList user={user} token={token} selectedSymbol={selectedSymbol} onSelectSymbol={setSelectedSymbol} />
          </Box>
        </Box>
        <StatusBar statusText={statusText} />

        {/* Dialogs */}
        <FixedCycleDialog
          open={fixedCycleDialogOpen}
          onClose={() => setFixedCycleDialogOpen(false)}
          totalBars={barData?.dates?.length || 0}
          initialValue={fixedCycleLength}
          initialUnit={fixedCycleUnit}
          dates={extendedBarData?.dates}
          onApply={(n, unit) => {
            setFixedCycleLength(n);
            setFixedCycleUnit(unit);
            setFixedCycleDialogOpen(false);
            // The dialog only collects the interval; the anchor is the next click on the chart.
            setDrawingMode('draw_fixed_cycle');
            setStatusText(`Fixed cycle of ${n} ${unit === 'days' ? 'calendar day(s)' : 'bar(s)'}: `
              + 'click a bar to anchor it. Esc to cancel.');
          }}
        />
        <LocationSettingsDialog
          open={isLocationSettingsOpen}
          onClose={() => setLocationSettingsOpen(false)}
          onSave={handleSaveLocationSettings}
          currentPreferences={locationPreferences}
        />
        <StrategyDesignDialog
          open={isStrategyDesignDialogOpen}
          onClose={() => setIsStrategyDesignDialogOpen(false)}
          onRun={handleStrategyDesignRun}
          barData={barData}
          extendedBarData={extendedBarData}
          onJournalAppend={handleJournalAppend}
        />
        <Strategy2DesignDialog
          open={isStrategy2DesignDialogOpen}
          onClose={() => setIsStrategy2DesignDialogOpen(false)}
          onRun={handleStrategy2DesignRun}
        />
        <TimeAndEventStrategyDialog 
          open={isTimeAndEventStrategyDialogOpen} 
          onClose={() => setTimeAndEventStrategyDialogOpen(false)} 
          onRun={handleTimeAndEventStrategyRun}
        />
        {barData && barData.dates && barData.dates.length > 0 && (
          <>
            <ElliottWaveDialog
              open={elliottOpen}
              onClose={() => setElliottOpen(false)}
              loading={elliottLoading}
              error={elliottError}
              result={elliottResult}
              overlayShown={elliottOverlayShown}
              onToggleOverlay={handleToggleElliottOverlay}
            />
            <LongitudeStudyDlg open={isStudyDialogOpen} onClose={() => setStudyDialogOpen(false)} onRun={handleRunSingleStudy} barData={extendedBarData} onJournalAppend={handleStudyReportJournal} />
            <PlanetFollowStudyDialog open={isPlanetFollowStudyDialogOpen} onClose={() => setPlanetFollowStudyDialogOpen(false)} onRun={handleRunSingleStudy} barData={extendedBarData} />
            <AspectsAreaHighlightDialog
              open={isAspectsAreaHighlightDialogOpen}
              onClose={() => setIsAspectsAreaHighlightDialogOpen(false)}
              barData={barData}
              extendedBarData={extendedBarData}
              onRun={handleAspectsAreaHighlightRun}
              onJournalAppend={handleJournalAppend}
            />
            <DeclinationStudyDialog open={isDeclinationStudyDialogOpen} onClose={() => setDeclinationStudyDialogOpen(false)} onRun={handleRunSingleStudy} barData={extendedBarData} onJournalAppend={handleStudyReportJournal} />
            <PlanetaryLinesDialog open={isPlanetaryLinesDialogOpen} onClose={() => setPlanetaryLinesDialogOpen(false)} onRun={handleRunPlanetaryLines} />
          </>
        )}
        <VisualUniverseDialog 
          open={isVisualUniverseDialogOpen} 
          onClose={() => setVisualUniverseDialogOpen(false)} 
          token={token} 
          initialDate={visualUniverseInitialDate}
        />
        <IntradayAnimationDialog
          open={isIntradayAnimationDialogOpen}
          onClose={() => setIntradayAnimationDialogOpen(false)}
        />
        <ChartComparisonDialog
          open={isChartComparisonDialogOpen}
          onClose={() => setChartComparisonDialogOpen(false)}
          barData={barData}
          symbol={selectedSymbol}
        />
        <PlanetGroupDialog open={isPlanetGroupDialogOpen} onClose={() => setPlanetGroupDialogOpen(false)} />
        <StrategyReportStudyDialog open={isStrategyReportStudyDialogOpen} onClose={() => setStrategyReportStudyDialogOpen(false)} />
        <StrategyReportStudyBDialog open={isStrategyReportStudyBDialogOpen} onClose={() => setStrategyReportStudyBDialogOpen(false)} />
        {/*
          barData/extendedBarData were never passed before, because the only control that
          opened this dialog was disabled — so the omission was invisible. The dialog guards
          against missing data and logs, rather than crashing, which means it would have
          opened and silently done nothing.
        */}
        <AggregateAreaHighlightDialog
          open={isAggregateAreaHighlightDialogOpen}
          onClose={() => setIsAggregateAreaHighlightDialogOpen(false)}
          barData={barData}
          extendedBarData={extendedBarData}
        />
        <PlanetCharacteristicsPlotDialog
          open={isPlanetCharacteristicsDialogOpen}
          onClose={() => setIsPlanetCharacteristicsDialogOpen(false)}
          onRunSeasonality={handleRunSeasonality}
          onRunCharacteristics={handleRunCharacteristicsPlot}
          onRunPivotAnalysis={async ({ pivotType, vicinity, minPivot, maxPivot, factorThreshold, futureThreshold, projectionMonths, asOfDate, setReportContent, switchToReport }) => {
            const symbol = barData?.symbol || selectedSymbol;
            if (!symbol) {
              alert('No symbol loaded. Please load a chart first.');
              return;
            }
            const mode = asOfDate ? ` [backtest as-of ${asOfDate}]` : '';
            console.log('[App.js] Pivot analysis requested:', pivotType, 'symbol:', symbol, mode);
            setReportContent('Running pivot analysis for ' + symbol + ' (' + pivotType + ')' + mode + '...\nPlease wait, this may take a minute.');
            switchToReport();
            try {
              const payload = { symbol, pivotType, vicinity, minPivot, maxPivot, factorThreshold, futureThreshold, projectionMonths };
              if (asOfDate) payload.asOfDate = asOfDate;
              const response = await api.post('/api/pivot-analysis', payload,
                { headers: { 'Authorization': `Bearer ${token}` } });
              const { report, error } = response.data;
              if (error) {
                setReportContent('ERROR: ' + error);
              } else {
                setReportContent(report || 'No report generated.');
              }
            } catch (err) {
              console.error('[App.js] Pivot analysis error:', err);
              setReportContent('ERROR: ' + (err.response?.data?.error || err.message));
            }
          }}
          barData={barData}
        />
        <AstroGraphDialog
          open={isAstroGraphDialogOpen}
          onClose={() => { setIsAstroGraphDialogOpen(false); setAstroGraphCommentary(''); }}
          onExecute={handleAstroGraphExecute}
          onExecuteAndStudy={handleAstroGraphExecuteAndStudy}
          onAnimate={handleAstroGraphAnimate}
          commentary={astroGraphCommentary}
          barData={barData}
        />
        <CyclesDialog 
          open={isCyclesDialogOpen} 
          onClose={() => setCyclesDialogOpen(false)} 
          bar1Date={bar1Date}
          bar2Date={bar2Date}
          onDisplay={handleDisplayCycles}
          onErase={handleEraseCycles}
          onManualDrawToggle={handleManualDrawToggle}
          onOpenVisualUniverse={handleOpenVisualUniverse}
        />
      <PatternConfigDialog
        open={isPatternConfigDialogOpen}
        onClose={() => setIsPatternConfigDialogOpen(false)}
        maConfigs={maConfigs}
        onApply={(newMaConfigs) => {
          setMaConfigs(newMaConfigs);
          setIsPatternConfigDialogOpen(false);
        }}
      />
      <TrialWelcomeDialog
        open={trialWelcomeOpen}
        onClose={() => setTrialWelcomeOpen(false)}
      />
      </Box>
    </ThemeProvider>
  );
}

export default App;